import React, { useState, useEffect } from 'react';
import { Box, TextField, Typography, Stack } from '@mui/material';
import { FieldProps, DateSlotsFieldSchema, FormOption } from '../types/Form';
import { FieldWrapper } from './FieldWrapper';
import { Dropdown } from './Dropdown';

interface DateValue {
  day: string;
  month: string;
  year: string;
}

const MONTH_OPTIONS: FormOption[] = [
  { label: 'Jan', value: '1' },
  { label: 'Feb', value: '2' },
  { label: 'Mar', value: '3' },
  { label: 'Apr', value: '4' },
  { label: 'May', value: '5' },
  { label: 'Jun', value: '6' },
  { label: 'Jul', value: '7' },
  { label: 'Aug', value: '8' },
  { label: 'Sep', value: '9' },
  { label: 'Oct', value: '10' },
  { label: 'Nov', value: '11' },
  { label: 'Dec', value: '12' }
];

const formatToDateTime = (dateValue: DateValue): string => {
  const { day, month, year } = dateValue;

  if (!day || !month || !year) {
    return '';
  }

  const paddedDay = day.padStart(2, '0');
  const paddedMonth = month.padStart(2, '0');

  return `${year}-${paddedMonth}-${paddedDay}`;
};

const getSpecificErrorMessage = (
  errorMessage: string | undefined,
  dateValue: DateValue
): string | undefined => {
  if (!errorMessage) return undefined;

  const missingFields = [
    !dateValue.year && 'year',
    !dateValue.month && 'month',
    !dateValue.day && 'day'
  ].filter(Boolean);

  if (missingFields.length === 0) return errorMessage;
  if (missingFields.length === 1)
    return `Date must include ${missingFields[0]}`;
  if (missingFields.length === 2)
    return `Date must include ${missingFields[0]} and ${missingFields[1]}`;

  return errorMessage;
};

const parseFromDateTime = (dateTime: string): DateValue => {
  if (!dateTime) {
    return { day: '', month: '', year: '' };
  }

  try {
    const parts = dateTime.split('-');
    if (parts.length !== 3) {
      return { day: '', month: '', year: '' };
    }

    const [year, month, day] = parts;

    const parsedYear = year === 'undefined' ? '' : year;
    const parsedMonth = month === 'undefined' ? '' : month;
    const parsedDay = day === 'undefined' ? '' : day;

    if (parsedYear && parsedMonth && parsedDay) {
      const yearNum = parseInt(parsedYear, 10);
      const monthNum = parseInt(parsedMonth, 10);
      const dayNum = parseInt(parsedDay, 10);

      if (!isNaN(yearNum) && !isNaN(monthNum) && !isNaN(dayNum)) {
        return {
          day: dayNum.toString(),
          month: monthNum.toString(),
          year: yearNum.toString()
        };
      }
    }

    return {
      day: parsedDay,
      month: parsedMonth,
      year: parsedYear
    };
  } catch {
    return { day: '', month: '', year: '' };
  }
};

export const DateSlots: React.FC<FieldProps<DateSlotsFieldSchema>> = ({
  name,
  value = '',
  field,
  errorMessage,
  onChange
}) => {
  const { label, subLabel, containerSx } = field;

  const [dateValue, setDateValue] = useState<DateValue>(() =>
    parseFromDateTime(value)
  );

  useEffect(() => {
    setDateValue(parseFromDateTime(value));
  }, [value]);

  const specificErrorMessage = getSpecificErrorMessage(errorMessage, dateValue);

  const handleDateChange = (fieldName: keyof DateValue, newValue: string) => {
    if (newValue && !/^\d+$/.test(newValue)) {
      return;
    }

    if (newValue && !validateInput(newValue)) {
      return;
    }

    const updatedDateValue = {
      ...dateValue,
      [fieldName]: newValue
    };

    setDateValue(updatedDateValue);

    const dateTime = formatToDateTime(updatedDateValue);
    if (dateTime) {
      onChange(dateTime);
    } else {
      const { day, month, year } = updatedDateValue;
      if (!day && !month && !year) {
        onChange('');
      } else if (value && formatToDateTime(parseFromDateTime(value))) {
        const invalidDate = `${year || 'undefined'}-${month || 'undefined'}-${
          day || 'undefined'
        }`;
        onChange(invalidDate);
      }
    }
  };

  const validateInput = (value: string): boolean => {
    if (!value) return true;
    const num = parseInt(value, 10);

    return num > 0;
  };

  const hasFieldError = (field: keyof DateValue): boolean => {
    if (errorMessage) {
      const fieldValue = dateValue[field];
      return !fieldValue;
    }

    const fieldValue = dateValue[field];

    if (fieldValue && !validateInput(fieldValue)) {
      return true;
    }

    return false;
  };

  return (
    <FieldWrapper
      label={label}
      subLabel={subLabel}
      errorMessage={specificErrorMessage}
      sx={containerSx}
    >
      <Stack spacing="3px">
        <Stack direction="row" spacing="25px">
          <Typography fontWeight="600" fontSize="18px" width="100px">
            Month
          </Typography>
          <Typography fontWeight="600" fontSize="18px" width="60px">
            Day
          </Typography>
          <Typography fontWeight="600" fontSize="18px" width="90px">
            Year
          </Typography>
        </Stack>
        <Stack direction="row" spacing="25px" alignItems="flex-end">
          <Box width="100px">
            <Dropdown
              name={`date-field-month-${name}`}
              value={dateValue.month}
              field={{
                options: MONTH_OPTIONS,
                buttonText: 'Month'
              }}
              error={hasFieldError('month')}
              onChange={(value) => handleDateChange('month', value as string)}
            />
          </Box>
          <TextField
            data-testid={`date-field-day-${name}`}
            value={dateValue.day}
            onChange={(e) => handleDateChange('day', e.target.value)}
            error={hasFieldError('day')}
            inputProps={{
              maxLength: 2
            }}
            sx={{
              width: '60px',
              '& .MuiOutlinedInput-root': {
                '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                  borderWidth: 3
                }
              }
            }}
            variant="outlined"
          />
          <TextField
            data-testid={`date-field-year-${name}`}
            value={dateValue.year}
            onChange={(e) => handleDateChange('year', e.target.value)}
            error={hasFieldError('year')}
            inputProps={{
              maxLength: 4
            }}
            sx={{
              width: '90px',
              '& .MuiOutlinedInput-root': {
                '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                  borderWidth: 3
                }
              }
            }}
            variant="outlined"
          />
        </Stack>
      </Stack>
    </FieldWrapper>
  );
};
